import "./base.css"

import { FieldValues, UseControllerProps, useController } from "react-hook-form"
import React, { KeyboardEvent, ReactNode, useCallback, useRef, useState } from "react"

import PhoneInputStyles from "./phone.module.css"
import SelectStyles from "./select.module.css"
import WrapWithError from "./wrap-with-error"
import classNames from "classnames"
import { useOnClickOutside } from "../../hooks/use-on-click-outside"

type PhoneInputProps<T extends FieldValues> = {
    options: { value: string; name: string; cc: string; ornament?: ReactNode }[]
    length?: number
    type: HTMLInputElement["type"]
    autocomplete?: HTMLInputElement["autocomplete"]
    width?: number | string
    height?: number
    maxHeight?: number
    autoSize?: boolean
    required?: boolean
    placeholder?: string
    nameCC: UseControllerProps<T>["name"]
    nameNumber: UseControllerProps<T>["name"]
    defaultValueCC?: UseControllerProps<T>["defaultValue"]
    defaultValueNumber?: UseControllerProps<T>["defaultValue"]
}

const PhoneInput = <T extends FieldValues>({
    options,
    nameCC,
    nameNumber,
    control,
    rules,
    defaultValueNumber,
    defaultValueCC,
    shouldUnregister,
    length,
    autocomplete,
    width,
    height,
    maxHeight,
    autoSize,
    required,
    placeholder,
}: PhoneInputProps<T> & Omit<UseControllerProps<T>, "name" | "defaultValue">) => {
    const {
        field: { onChange, onBlur, value, ref: controllerRef, ...rest },
        fieldState: { error, isTouched },
    } = useController({
        name: nameNumber,
        control,
        rules,
        defaultValue: defaultValueNumber,
        shouldUnregister,
    })

    const {
        field: { onChange: onChangeCC, ref: controllerRefCC },
    } = useController({
        name: nameCC,
        control,
        defaultValue: defaultValueCC,
        shouldUnregister,
    })

    const [inputFocus, setInputFocus] = useState<boolean>(false)
    let ref = useRef<HTMLInputElement | null>(null)

    const [inFocus, setInFocus] = useState<boolean>(false)
    const [active, setActive] = useState<number>(0)
    const optionsRef = useRef<HTMLDivElement | null>(null)
    const blockOnMouseOver = useRef<boolean>(false)

    const scrollIntoView = useCallback((index: number) => {
        const optionsEl = optionsRef.current
        if (!optionsEl) return
        const focusedEl = Array.from(optionsEl.children)[index] as HTMLElement
        const optionsRec = optionsEl.getBoundingClientRect()
        const focusedRect = focusedEl.getBoundingClientRect()
        const overScroll = focusedEl.offsetHeight / 3

        if (focusedRect.bottom + overScroll > optionsRec.bottom) {
            optionsEl.scrollTo(
                0,
                Math.min(
                    focusedEl.offsetTop + focusedEl.clientHeight - optionsEl.offsetHeight + overScroll,
                    optionsEl.scrollHeight
                )
            )
        } else if (focusedRect.top - overScroll < optionsRec.top) {
            optionsEl.scrollTo(0, Math.max(focusedEl.offsetTop - overScroll, 0))
        }
    }, [])

    const focusValue = useCallback(
        (direction: "next" | "previous") => {
            let index = active || 0
            switch (direction) {
                case "next":
                    index += 1
                    if (index >= options.length) index = 0
                    break
                case "previous":
                    index -= 1
                    if (index < 0) index = options.length - 1
                    break
                default:
                    break
            }
            setActive(index)
            scrollIntoView(index)
        },
        [setActive, options, active, scrollIntoView]
    )

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLButtonElement>) => {
            if (event.defaultPrevented) {
                return
            }

            blockOnMouseOver.current = true

            switch (event.key) {
                case "ArrowUp":
                    focusValue("previous")
                    break
                case "ArrowDown":
                    focusValue("next")
                    break
                case "Enter":
                case " ":
                    setInFocus(false)
                    onChangeCC(options[active].value)
                    ref.current?.select()
                    break
                case "Tab":
                    if (!inFocus) return
                    setInFocus(false)
                    onChangeCC(options[active].value)
                    if (event.shiftKey) break
                    ref.current?.select()
                    break
                case "Escape":
                    setInFocus(false)
                    ref.current?.select()
                    break
                default:
                    return
            }
            event.preventDefault()
        },
        [active, options, focusValue, blockOnMouseOver, inFocus]
    )

    const onClick = (value: string, index: number) => {
        setInFocus(false)
        setActive(index)
        onChangeCC(value)
    }

    const onMouseOver = (index: number) => {
        if (blockOnMouseOver.current) return

        setActive(index)
    }

    const onMouseMove = () => {
        blockOnMouseOver.current = false
    }

    useOnClickOutside(() => inFocus && setInFocus(false), [optionsRef], [inFocus])

    return (
        <div
            className={classNames("inputBlock", {
                error: error,
                wasInFocus: (isTouched && value) || value,
            })}
        >
            <WrapWithError error={error} id={nameNumber}>
                <div
                    className={classNames("input", PhoneInputStyles.wrapper, { [PhoneInputStyles.focus]: inputFocus })}
                >
                    <div
                        className={classNames(SelectStyles.select, {
                            [SelectStyles.open]: inFocus,
                            [SelectStyles.empty]: !value,
                        })}
                    >
                        <button
                            type="button"
                            className={PhoneInputStyles.button}
                            onClick={() => setInFocus(true)}
                            onKeyDown={e => onKeyDown(e)}
                        >
                            {options[active].ornament && (
                                <div className={SelectStyles.ornament}>{options[active].ornament}</div>
                            )}
                            <span>{options[active].cc}</span>
                            <input type="hidden" name={nameCC} value={options[active].value} ref={controllerRefCC} />
                            <i className="lavita-icon"></i>
                        </button>
                    </div>
                    <div className={classNames(SelectStyles.options, PhoneInputStyles.options)}>
                        <div ref={optionsRef} style={{ maxHeight }}>
                            {options.map((option, index) => (
                                <button
                                    type="button"
                                    key={option.value}
                                    onClick={() => onClick(option.value, index)}
                                    onFocus={onMouseMove}
                                    onMouseOver={() => onMouseOver(index)}
                                    onMouseMove={onMouseMove}
                                    className={classNames({ [SelectStyles.selectActive]: active === index })}
                                    style={{ height: height }}
                                >
                                    {option.ornament && <div className={SelectStyles.ornament}>{option.ornament}</div>}
                                    <div className={PhoneInputStyles.label}>
                                        <div>{option.name}</div>
                                        <div>{option.cc}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <input
                        type="tel"
                        id={nameNumber}
                        value={value}
                        onBlur={() => {
                            onBlur()
                            setInputFocus(false)
                        }}
                        onFocus={() => setInputFocus(true)}
                        onChange={onChange}
                        autoComplete={autocomplete}
                        maxLength={length}
                        ref={element => {
                            ref.current = element
                            controllerRef(element)
                        }}
                        style={{ width: width, height: height }}
                        size={autoSize && (length || value?.length)}
                        placeholder={placeholder}
                        required={required}
                        aria-invalid={!!error}
                        aria-errormessage={"err-" + nameNumber}
                        {...rest}
                    />
                </div>
            </WrapWithError>
        </div>
    )
}

export default PhoneInput
